import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import StatusBar from '@/designs-dev/device/StatusBar';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import AccountBar, { type Account } from '@/karehero-app/access/AccountBar';
import FactList from '@/karehero-app/tell-us-once/intake/FactList';
import { REVIEW_ALL } from '@/karehero-app/tell-us-once/intake/content';
import { PAGE_HEADER_TOP } from '@/karehero-app/shell/layout';

const SCREEN_PX = 3;

const frostedFooter = {
  backgroundColor: (theme: any) =>
    alpha(theme.palette.background.default, 0.72),
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
};

// Option 1's ending: everything in one read, ordered by how sure we are rather
// than by topic. Same grouped list as option 2's review, so the two endings
// differ in what they say, not in how they are drawn.
const ReviewAllScreen = ({
  onDone,
  account,
}: {
  onDone?: () => void;
  /** The app bar. See StepScreen's own `account`. */
  account?: Account;
}) => {
  return (
    <ScreenRoot relative>
      <StatusBar />
      {account && <AccountBar {...account} />}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          px: SCREEN_PX,
          pb: '96px',
        }}
      >
        <Box sx={{ pt: PAGE_HEADER_TOP }}>
          <Stack spacing={3}>
            <PageHeader title={REVIEW_ALL.title} variant='h2' />

            {/* What the colours mean, said once at the top rather than guessed
                at per row. */}
            <Stack spacing={0.75}>
              {REVIEW_ALL.key.map((entry) => (
                <Stack
                  key={entry.label}
                  sx={{ alignItems: 'center' }}
                  direction='row'
                  spacing={1}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      flexShrink: 0,
                      borderRadius: '50%',
                      bgcolor:
                        entry.tone === 'attention'
                          ? 'custom.coral.500'
                          : 'custom.sunflower.500',
                    }}
                  />
                  <Typography
                    sx={{ color: 'text.secondary' }}
                    variant='caption'
                  >
                    {entry.label}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <FactList groups={REVIEW_ALL.groups} hideUnanswered />
          </Stack>
        </Box>
      </Box>

      <Box
        sx={[
          {
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            px: SCREEN_PX,
            py: 1.5,
          },
          frostedFooter,
        ]}
      >
        <Button
          variant='primary'
          size='lg'
          isFullWidth
          ariaLabel={REVIEW_ALL.primary}
          onPress={onDone}
        >
          {REVIEW_ALL.primary}
        </Button>
      </Box>
    </ScreenRoot>
  );
};

export default ReviewAllScreen;
